<template>
    <div>
        <CompassFormItem
            :label="$t.__('common.labels.address')"
            :error="getErrorMsg"
            :orientation="orientation"
        >
            <CompassInput
                ref="jawgInput"
                v-model="query"
                prefixIcon="location"
                :error="!!errorMsg"
                @input="$emit('input')"
                @update:modelValue="handleUpdateValue"
            />
        </CompassFormItem>
        <CompassPopover
            :isVisible="isVisible"
            :triggerRef="triggerRefEl"
            placement="bottom-start"
            :isTriggerWidth="true"
            @close="isVisible = false"
        >
            <div v-show="isLoading" class="autocomplete-address__loader">
                <CompassLoader :size="CompassEnums.LoaderSize.SMALL" />
            </div>
            <div
                v-show="!isLoading"
                ref="autocompleteAddressResults"
                class="autocomplete-address__results typographies-paragraph-medium-regular"
            />
        </CompassPopover>
    </div>
</template>

<script>
import JawgJSLoader from '@jawg/js-loader';
import Env from 'Services/Env';
import { formatAddressForJawgForApi } from 'Helpers/addressHelpers';
import { debounce } from 'lodash';

export default {
    props: {
        errorMsg: {
            type: String,
            default: '',
        },
        orientation: {
            type: String,
            default: 'vertical',
        },
    },
    emits: ['update:address', 'input'],
    data() {
        return {
            jawgToken: Env.get('jawg').token,
            jawgLoader: null,
            jawgPlacesPromise: null,
            query: '',
            isVisible: false,
            isLoading: false,
            placesInput: null,
            triggerRefEl: null,
        };
    },
    computed: {
        getErrorMsg() {
            if (!this.errorMsg) {
                return '';
            }

            return this.query
                ? this.$t.__('document.DocFormSettingsDialog.AutocompleteAddress.invalid_address')
                : this.errorMsg;
        },
    },
    watch: {
        isVisible(newValue) {
            if (!newValue && this.placesInput) {
                this.placesInput.destroy();
                this.placesInput = null;
            }
        },
    },
    mounted() {
        this.triggerRefEl = this.$refs.jawgInput;
        this.jawgLoader = new JawgJSLoader({ accessToken: this.jawgToken });
        this.jawgPlacesPromise = window.JawgPlaces
            ? Promise.resolve(window.JawgPlaces)
            : this.jawgLoader.loadJawgPlaces();
    },
    beforeUnmount() {
        this.placesInput?.destroy();
    },
    methods: {
        initPlacesInput(jawgPlaces) {
            this.placesInput = new jawgPlaces.Input({
                input: this.$refs.jawgInput.$refs.compassInput,
                noResultsMessage: this.$t.__('common.labels.no_result'),
                onFeatures: () => {
                    this.isVisible = true;
                },
                onClick: (feature) => {
                    this.isVisible = false;
                    const address = formatAddressForJawgForApi(feature);
                    this.$emit('update:address', address);
                    this.query = address.label;
                },
                onClose: () => {
                    this.isVisible = false;
                },
                resultContainer: this.$refs.autocompleteAddressResults,
            });
        },
        handleUpdateValue: debounce(async function (value) {
            this.query = value;
            if (!value) {
                return;
            }
            this.isVisible = true;
            this.isLoading = true;
            try {
                const jawgPlaces = await this.jawgPlacesPromise;
                if (!this.placesInput) {
                    await this.$nextTick();
                    this.initPlacesInput(jawgPlaces);
                }
                const featureCollection = await this.placesInput.search(value);
                featureCollection.features = featureCollection.features.filter(({ properties }) => {
                    return properties?.postalcode && properties?.locality;
                });
                this.placesInput.showResults(featureCollection);
            } finally {
                this.isLoading = false;
            }
        }, 200),
    },
};
</script>

<style lang="scss" scoped>
.autocomplete-address {
    &__loader {
        align-items: center;
        display: flex;
        justify-content: center;
        padding: var(--spacing-2);
    }

    &__results {
        max-height: 312px;
        overflow-y: auto;
    }
}
</style>
